import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { AbasSublinhadas, PageHeader, SelectBusca, type Aba } from "@/components/padroes";
import { useAtualizacaoNF, STATUS_AGUARDANDO_OCR } from "@/context/useAtualizacaoNF";
import { useAuth } from "@/context/AuthContext";
import { useNomeBonificacao } from "@/hooks/useNomeBonificacao";
import { StatusBadge } from "@/components/StatusBadge";
import { formatBRL, formatDate } from "@/lib/format";
import type { NFStatus, Departamento, NotaFiscalResumo } from "@/data/mock";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Search, Download, Calendar, Layers, Send, ListFilter, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { triggerDownload } from "@/lib/download";
import { usePaginado, useDebounce, montarQuery, type Filtros, type Pagina } from "@/hooks/usePaginado";
import { Paginacao } from "@/components/Paginacao";

const ALL_STATUSES: NFStatus[] = [
  "Pendente", "A validar", "Válido", "CNPJ Inválido", "Erro OCR", "Em análise", "Com ressalvas", "Aprovada", "Recusada", "Cancelada",
];

// Abas = grupos de status (o backend aceita vários separados por vírgula); o
// select "Status" continua filtrando um status exato.
// "A validar" = o que espera o RH (validado pelo OCR ou em análise manual), o
// mesmo critério do contador da sidebar; "Em processamento" = OCR em curso ou com problema.
type AbaNF = "todas" | "validar" | "processamento" | "aprovadas" | "recusadas";
const ABAS: { value: AbaNF; label: string; status: string }[] = [
  { value: "todas", label: "Todas", status: "" },
  { value: "validar", label: "A validar", status: "Válido,Em análise" },
  { value: "processamento", label: "Em processamento", status: "A validar,Pendente,CNPJ Inválido,Erro OCR,Com ressalvas" },
  { value: "aprovadas", label: "Aprovadas", status: "Aprovada" },
  { value: "recusadas", label: "Recusadas", status: "Recusada" },
];

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

/** "2026-09" → "set/2026". */
const mesCurto = (yyyymm: string | null | undefined) => {
  if (!yyyymm) return "—";
  const [y, m] = yyyymm.split("-");
  return `${MESES[Number(m) - 1] ?? m}/${y}`;
};

/** Rótulo do botão de ZIP: "de setembro" (ano só se não for o atual) ou "de 02/10". */
const rotuloZip = (mes: string, data: string) => {
  if (mes) {
    const [y, m] = mes.split("-").map(Number);
    const nome = new Date(y, m - 1, 1).toLocaleDateString("pt-BR", { month: "long" });
    return `Baixar ZIP de ${nome}${y !== new Date().getFullYear() ? ` de ${y}` : ""}`;
  }
  if (data) {
    const [, m, d] = data.split("-");
    return `Baixar ZIP de ${d}/${m}`;
  }
  return "Baixar ZIP";
};

/**
 * Contador de cada aba com os demais filtros aplicados: uma consulta de 1 item
 * por aba, aproveitando o `total` da listagem paginada.
 */
function useContagemAbas(filtros: Filtros) {
  const [contagens, setContagens] = useState<Partial<Record<AbaNF, number>>>({});
  const chave = JSON.stringify(filtros);
  const seq = useRef(0);

  const recarregar = useCallback(async () => {
    const minha = ++seq.current;
    const base = JSON.parse(chave) as Filtros;
    try {
      const totais = await Promise.all(
        ABAS.map((a) =>
          api
            .get<Pagina<NotaFiscalResumo>>(
              `/nfs${montarQuery({ ...base, status: a.status, page: 1, pageSize: 1 })}`,
            )
            .then((r) => r.total),
        ),
      );
      if (minha !== seq.current) return;
      setContagens(Object.fromEntries(ABAS.map((a, i) => [a.value, totais[i]])));
    } catch {
      // Sem contadores as abas só não mostram o número; o erro da lista já avisa.
    }
  }, [chave]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  return { contagens, recarregar };
}

/** Campo de filtro no formato do protótipo: ícone + rótulo + controle. */
const CampoFiltro = ({ icone, rotulo, children }: { icone: ReactNode; rotulo: string; children: ReactNode }) => (
  <label className="flex h-[38px] items-center gap-2 whitespace-nowrap rounded-lg border bg-card px-3 text-sm [&>svg]:h-[15px] [&>svg]:w-[15px] [&>svg]:shrink-0 [&>svg]:text-muted-foreground">
    {icone}
    <span className="text-muted-foreground">{rotulo}</span>
    {children}
  </label>
);

const RHListaNFs = () => {
  const nav = useNavigate();
  const { user: me } = useAuth();
  const nomeBonificacao = useNomeBonificacao();
  const [busca, setBusca] = useState("");
  /** "all", um status do select ou o grupo de uma aba ("Válido,Em análise"). */
  const [status, setStatus] = useState<string>("all");
  const [mes, setMes] = useState("");
  const [data, setData] = useState("");
  const [dep, setDep] = useState("all");
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [downloadingZip, setDownloadingZip] = useState(false);

  const handleDownloadZip = async () => {
    if (!mes && !data) return;
    setDownloadingZip(true);
    try {
      const query = mes ? `mes=${mes}` : `data=${data}`;
      const { blob, filename } = await api.getFile(`/nfs/download-zip?${query}`);
      triggerDownload(blob, filename);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao baixar ZIP");
    } finally {
      setDownloadingZip(false);
    }
  };

  useEffect(() => {
    api.get<Departamento[]>("/departamentos").then(setDepartamentos).catch(() => {});
  }, []);

  // Filtros no servidor; "data" é o dia de envio no horário de Brasília.
  const buscaAtrasada = useDebounce(busca.trim());
  const filtrosBase = {
    q: buscaAtrasada,
    mesReferencia: mes,
    data,
    departamento: dep === "all" ? "" : dep,
  };
  const lista = usePaginado<NotaFiscalResumo>("/nfs", {
    ...filtrosBase,
    status: status === "all" ? "" : status,
  });
  const { contagens, recarregar: recarregarContagens } = useContagemAbas(filtrosBase);
  const recarregarLista = lista.recarregar;
  const recarregarTudo = useCallback(() => {
    void recarregarLista();
    void recarregarContagens();
  }, [recarregarLista, recarregarContagens]);
  useAtualizacaoNF(recarregarTudo, lista.items.some((n) => n.status === STATUS_AGUARDANDO_OCR));

  const limpar = () => {
    setBusca(""); setStatus("all"); setMes(""); setData(""); setDep("all");
  };

  // Um status único escolhido no select (ex.: "Com ressalvas") deixa nenhuma aba ativa.
  const abaAtiva: AbaNF | "outro" =
    status === "all" ? "todas" : (ABAS.find((a) => a.status === status)?.value ?? "outro");

  const abas: Aba<AbaNF | "outro">[] = ABAS.map((a) => ({
    value: a.value,
    label: a.label,
    contador: contagens[a.value] ?? null,
    aviso: a.value === "validar",
  }));

  const temFiltro = !!(busca || status !== "all" || mes || data || dep !== "all");

  return (
    <RoleGuard role={["rh", "financeiro"]}>
      <AppLayout title="Notas Fiscais">
        <div className="mx-auto flex max-w-7xl flex-col gap-4">
          <PageHeader
            titulo="Notas Fiscais"
            subtitulo="Valide, aprove e baixe as notas enviadas pelos colaboradores PJ."
            acoes={
              <Button
                variant="outline"
                onClick={handleDownloadZip}
                disabled={(!mes && !data) || downloadingZip}
                title={
                  mes ? "Baixar NFs da competência em ZIP"
                  : data ? "Baixar NFs enviadas na data em ZIP"
                  : "Selecione uma competência ou data de envio para baixar o ZIP"
                }
              >
                <Download />
                {downloadingZip ? "Baixando..." : rotuloZip(mes, data)}
              </Button>
            }
          />

          <AbasSublinhadas
            value={abaAtiva}
            onChange={(v) => setStatus(ABAS.find((a) => a.value === v)?.status || "all")}
            abas={abas}
          />

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-[1_1_280px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por colaborador ou número..."
                className="h-[38px] pl-9"
              />
            </div>
            <CampoFiltro icone={<Calendar />} rotulo="Competência">
              <input
                type="month"
                value={mes}
                onChange={(e) => {
                  setMes(e.target.value);
                  if (e.target.value) setData("");
                }}
                className="bg-transparent font-medium tabular-nums outline-none"
                aria-label="Competência"
              />
            </CampoFiltro>
            <SelectBusca
              value={dep}
              onChange={setDep}
              aria-label="Departamento"
              placeholderBusca="Buscar departamento..."
              className="h-[38px] w-auto max-w-[260px] whitespace-nowrap rounded-lg bg-card font-medium"
              prefixo={
                <>
                  <Layers className="h-[15px] w-[15px] shrink-0 text-muted-foreground" />
                  <span className="font-normal text-muted-foreground">Depto.</span>
                </>
              }
              opcoes={[
                { value: "all", label: "Todos" },
                ...departamentos.map((d) => ({ value: d.name, label: d.name })),
              ]}
            />
            <CampoFiltro icone={<Send />} rotulo="Envio">
              <input
                type="date"
                value={data}
                onChange={(e) => {
                  setData(e.target.value);
                  if (e.target.value) setMes("");
                }}
                className="bg-transparent font-medium tabular-nums outline-none"
                aria-label="Data de envio"
              />
            </CampoFiltro>
            {/* Com um grupo de aba ativo o select mostra "Todos": a aba já indica o filtro. */}
            <Select value={status.includes(",") ? "all" : status} onValueChange={setStatus}>
              <SelectTrigger className="h-[38px] w-auto gap-2 whitespace-nowrap rounded-lg bg-card" aria-label="Status">
                <ListFilter className="h-[15px] w-[15px] shrink-0 text-muted-foreground" />
                <span className="text-muted-foreground">Status</span>
                <span className="font-medium">
                  <SelectValue />
                </span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {ALL_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              className="px-3 text-[13px] text-muted-foreground"
              onClick={limpar}
              disabled={!temFiltro}
            >
              Limpar filtros
            </Button>
          </div>

          <section className="overflow-hidden rounded-xl border bg-card">
            <Table className="min-w-[960px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Colaborador</TableHead>
                  <TableHead>Tomador</TableHead>
                  <TableHead>Mês ref.</TableHead>
                  <TableHead>Departamento</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Enviada</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {lista.items.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={8} className="py-12 text-center text-sm text-muted-foreground">
                      {lista.carregando ? "Carregando..." : "Nenhuma NF encontrada com esses filtros"}
                    </TableCell>
                  </TableRow>
                ) : (
                  lista.items.map((n) => (
                    <TableRow
                      key={n.id}
                      className="cursor-pointer"
                      onClick={() => nav(n.colaboradorId === me?.id ? `/colaborador/notas/${n.id}` : `/rh/notas/${n.id}`)}
                    >
                      <TableCell className="max-w-[280px]">
                        <div className="truncate font-medium">{n.colaboradorNome}</div>
                        <div className="text-xs text-muted-foreground">
                          NF nº {n.numero} ·{" "}
                          <span className={cn(n.tipo === "bonificacao" && "text-primary-text")}>
                            {n.tipo === "bonificacao" ? nomeBonificacao : "Salário"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[220px] truncate">{n.tomadorNome ?? "—"}</TableCell>
                      <TableCell className="whitespace-nowrap">{mesCurto(n.mesReferencia)}</TableCell>
                      <TableCell className="text-muted-foreground">{n.departamento || "—"}</TableCell>
                      <TableCell className="whitespace-nowrap text-right font-semibold">{formatBRL(n.valor)}</TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">{formatDate(n.dataEnvio)}</TableCell>
                      <TableCell><StatusBadge status={n.status} /></TableCell>
                      <TableCell className="pl-0 pr-3 text-muted-foreground/70">
                        <ChevronRight className="h-4 w-4" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <div className="border-t px-4 py-3 text-[13px]">
              <Paginacao
                page={lista.page}
                pageSize={lista.pageSize}
                total={lista.total}
                onPage={lista.setPage}
                carregando={lista.carregando}
                rotulo="notas"
              />
            </div>
          </section>
        </div>
      </AppLayout>
    </RoleGuard>
  );
};

export default RHListaNFs;
